import styled from 'styled-components';
import getThemeByProps from '@userzoomlibraries/toolkit-theme-manager/get-theme-by-props';
import React, { useRef, useState, useMemo, useCallback, useEffect, useImperativeHandle } from 'react';
import isDefinedAndNotNull from '@userzoomlibraries/toolkit-js-utils/is-defined-and-not-null';
import isFunction from '@userzoomlibraries/toolkit-js-utils/is-function';
import useFocusable from '@userzoomlibraries/toolkit-react-utils/use-focusable';
import useAriaAttributes from '@userzoomlibraries/toolkit-react-utils/use-aria-attributes';
import useDataAttributes from '@userzoomlibraries/toolkit-react-utils/use-data-attributes';
import useProps from '@userzoomlibraries/toolkit-react-utils/use-props';
import useComparePrevious from '@userzoomlibraries/toolkit-react-utils/use-compare-previous';
import Tag from '@userzoomlibraries/toolkit-tag';
import { e as Container, P as Prefix, T as TYPES, S as Suffix, f as TAGS_PROP_TYPES, g as TAGS_DEFAULT_PROPS } from './index.styled8.js';
import { jsxs, jsx } from 'react/jsx-runtime';

var _excluded = ["className", "prefix", "suffix", "invalid", "defaultValue", "value", "avoidCreateTagOnBlur", "placeholder", "onFocus", "onBlur", "onChange", "onTagCreated", "onTagRemoved"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var INPUT_SIZE_MARGIN = 6;
var InputTags$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
      prefix = _ref.prefix,
      suffix = _ref.suffix,
      invalid = _ref.invalid,
      defaultValue = _ref.defaultValue,
      value = _ref.value,
      avoidCreateTagOnBlur = _ref.avoidCreateTagOnBlur,
      placeholder = _ref.placeholder,
      onFocus = _ref.onFocus,
      onBlur = _ref.onBlur,
      onChange = _ref.onChange,
      onTagCreated = _ref.onTagCreated,
      onTagRemoved = _ref.onTagRemoved,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  var ariaAttributes = useAriaAttributes(otherProps, {
    excludedAttributes: ['aria-disabled', 'aria-invalid']
  });
  var dataAttributes = useDataAttributes(otherProps);
  var inputProps = useProps(otherProps, {
    excluded: ['data-*', 'aria-*']
  });
  var inputTagsRef = useRef();
  var inputTagsWrapperRef = useRef();
  var innerInputRef = useRef();

  var _useState = useState(defaultValue || []),
      _useState2 = _slicedToArray(_useState, 2),
      uncontrolledTags = _useState2[0],
      setUncontrolledTags = _useState2[1];

  var isControlledInput = useMemo(function () {
    if (isDefinedAndNotNull(defaultValue) && !isDefinedAndNotNull(value)) {
      return false;
    }

    if (!isDefinedAndNotNull(defaultValue) && isDefinedAndNotNull(value)) {
      return true;
    }

    if (!isDefinedAndNotNull(defaultValue) && !isDefinedAndNotNull(value)) {
      return false;
    }

    console.error("Warning: ".concat(InputTags$1.displayName, " contains an input with both value and defaultValue props.\n      Input elements must be either controlled or uncontrolled (specify either the value prop, or the defaultValue prop, but not both).\n      Decide between using a controlled or uncontrolled input element and remove one of these props. More info: https://reactjs.org/link/controlled-components"));
    return false;
  }, [useComparePrevious(value), useComparePrevious(defaultValue)]);
  var shouldShowPlaceholder = useMemo(function () {
    return isControlledInput && value.length === 0 || !isControlledInput && uncontrolledTags.length === 0;
  }, [isControlledInput, value, uncontrolledTags]); // Focusable props for input

  var inputFocusableProps = useFocusable(innerInputRef.current, inputProps); // Only apply the focusable props for the container if the input is disabled to be able to focus it

  var containerFocusableProps = inputFocusableProps['aria-disabled'] ? {
    'aria-disabled': inputFocusableProps['aria-disabled'],
    tabIndex: inputFocusableProps.tabIndex
  } : {};
  var prefixToProcess = useMemo(function () {
    if (isDefinedAndNotNull(prefix)) {
      if (isFunction(prefix)) {
        return prefix;
      }

      return function () {
        return prefix;
      };
    }

    return null;
  }, [prefix]);
  var suffixToProcess = useMemo(function () {
    if (isDefinedAndNotNull(suffix)) {
      if (isFunction(suffix)) {
        return suffix;
      }

      return function () {
        return suffix;
      };
    }

    return null;
  }, [suffix]);
  var createTag = useCallback(function (tagContent) {
    if (tagContent) {
      /*
        In case of uncontrolled input, we create the new tag in the internal array.
        In case of controlled input, we call the onChange callback with the new array of tags and the new tag "created".
      */
      var newValue;

      if (!isControlledInput) {
        newValue = [].concat(_toConsumableArray(uncontrolledTags), [tagContent]);
        setUncontrolledTags(newValue);
      } else {
        newValue = [].concat(_toConsumableArray(value), [tagContent]);
      } // Clear the input


      innerInputRef.current.value = ''; // Reset the input width

      innerInputRef.current.style.width = "calc(".concat(innerInputRef.current.value.length, "ch + ").concat(INPUT_SIZE_MARGIN, "px)");
      onTagCreated({
        tagCreated: tagContent
      });
      onChange({
        value: newValue,
        tagCreated: tagContent
      });
    }
  }, [value, uncontrolledTags, isControlledInput, onTagCreated, onChange]);
  var removeTag = useCallback(function (indexToRemove) {
    var tagsToUse = isControlledInput ? value : uncontrolledTags;
    var tagToRemove = tagsToUse[indexToRemove];
    var newValue = tagsToUse.filter(function (_, index) {
      return index !== indexToRemove;
    });

    if (!isControlledInput) {
      setUncontrolledTags(newValue);
    }

    if (newValue.length === 0) {
      innerInputRef.current.style.width = '100%';
    } else {
      innerInputRef.current.style.width = "calc(".concat(innerInputRef.current.value.length, "ch + ").concat(INPUT_SIZE_MARGIN, "px)");
    }

    onTagRemoved({
      tagRemoved: tagToRemove
    });
    onChange({
      value: newValue,
      tagRemoved: tagToRemove
    });
  }, [value, uncontrolledTags, isControlledInput, onTagRemoved, onChange]);
  var onFocusContainerHandler = useCallback(function (event) {
    // if the relatedTarget is not inside the container, we come from outside the container, so call the onFocus
    if (!inputTagsRef.current.contains(event.relatedTarget)) {
      onFocus(event);
    }
  }, [onFocus]);
  var onBlurContainerHandler = useCallback(function (event) {
    // if the relatedTarget is not inside the container, we are outside the container, so call the onBlur
    if (!inputTagsRef.current.contains(event.relatedTarget)) {
      onBlur(event);
    }
  }, [onBlur]);
  var onClickContainerHandler = useCallback(function (event) {
    // If we click at any element inside the container, we have to focus the input.
    if (inputTagsRef.current.contains(event.target)) {
      innerInputRef.current.focus();
    }
  }, []);
  var onInputFocusHandler = useCallback(function () {
    if (isControlledInput && value.length === 0 || !isControlledInput && uncontrolledTags.length === 0) {
      innerInputRef.current.style.width = '100%';
    } else {
      innerInputRef.current.style.width = "calc(".concat(innerInputRef.current.value.length, "ch + ").concat(INPUT_SIZE_MARGIN, "px)");
    }
  }, [isControlledInput, uncontrolledTags, value]);
  var onInputBlurHandler = useCallback(function (event) {
    if (!avoidCreateTagOnBlur) {
      var newTagContent = event.target.value;
      createTag(newTagContent);
    } else {
      // Clear the input
      innerInputRef.current.value = '';

      if (isControlledInput && value.length === 0 || !isControlledInput && uncontrolledTags.length === 0) {
        innerInputRef.current.style.width = '100%';
      } else {
        innerInputRef.current.style.width = "calc(".concat(innerInputRef.current.value.length, "ch + ").concat(INPUT_SIZE_MARGIN, "px)");
      }
    }
  }, [avoidCreateTagOnBlur, isControlledInput, uncontrolledTags, value, createTag]);
  var onInputChangeHandler = useCallback(function (event) {
    var target = event.target;
    var newValue = target.value;

    if ((isControlledInput && value.length === 0 || !isControlledInput && uncontrolledTags.length === 0) && newValue.length === 0) {
      innerInputRef.current.style.width = '100%';
    } else {
      var _inputTagsWrapperRef$ = inputTagsWrapperRef.current.getBoundingClientRect(),
          inputTagsWrapperWidth = _inputTagsWrapperRef$.width;

      var tmpElement = document.createElement('div');
      tmpElement.style.opacity = 0;
      tmpElement.style.width = "calc(".concat(newValue.length, "ch + ").concat(INPUT_SIZE_MARGIN, "px)");
      document.body.append(tmpElement);

      var _tmpElement$getBoundi = tmpElement.getBoundingClientRect(),
          tmpElementWidth = _tmpElement$getBoundi.width;

      document.body.removeChild(tmpElement);

      if (tmpElementWidth >= inputTagsWrapperWidth) {
        innerInputRef.current.style.width = '100%';
      } else {
        innerInputRef.current.style.width = "calc(".concat(newValue.length, "ch + ").concat(INPUT_SIZE_MARGIN, "px)");
      }
    }
  }, [isControlledInput, uncontrolledTags, value]);
  var onInputKeyDownHandler = useCallback(function (event) {
    var newTagContent = event.target.value;
    var isEnterKeyPressed = event.key === 'Enter' || event.code === 'Enter' || event.keyCode === 13;
    var isBackspaceKeyPressed = event.key === 'Backspace' || event.code === 'Backspace' || event.keyCode === 8;

    if (isEnterKeyPressed) {
      event.preventDefault();
      createTag(newTagContent);
    } else if (isBackspaceKeyPressed) {
      // In case that we don't have content for new tag and backspace is pressed, we remove the previous tag.
      if (!newTagContent) {
        var tagsToUse = isControlledInput ? value : uncontrolledTags;
        removeTag(tagsToUse.length - 1);
      } else {
        // In case that we have content and backspace is pressed, we have to update the width of the input.
        innerInputRef.current.style.width = "calc(".concat(newTagContent.length, "ch + ").concat(INPUT_SIZE_MARGIN, "px)");
      }
    }
  }, [isControlledInput, value, uncontrolledTags, createTag, removeTag]);
  useEffect(function () {
    if (isControlledInput && value.length === 0 || !isControlledInput && uncontrolledTags.length === 0) {
      innerInputRef.current.style.width = '100%';
    } else if (isControlledInput && value.length > 0) {
      innerInputRef.current.style.width = "calc(".concat(value.length, "ch + ").concat(INPUT_SIZE_MARGIN, "px)");
    } else {
      innerInputRef.current.style.width = "calc(".concat(uncontrolledTags.length, "ch + ").concat(INPUT_SIZE_MARGIN, "px)");
    }
  }, [isControlledInput, value, uncontrolledTags]);
  useImperativeHandle(ref, function () {
    return inputTagsRef.current;
  });
  var tagsToProcess = isControlledInput ? value : uncontrolledTags;
  return /*#__PURE__*/jsxs(Container, _objectSpread(_objectSpread({
    ref: inputTagsRef,
    className: "gz-input-wrapper ".concat(className),
    "aria-invalid": invalid,
    onFocus: onFocusContainerHandler,
    onBlur: onBlurContainerHandler,
    onClick: onClickContainerHandler // eslint-disable-next-line react/jsx-props-no-spreading

  }, containerFocusableProps), {}, {
    children: [prefixToProcess && /*#__PURE__*/jsx(Prefix, {
      children: prefixToProcess({
        disabled: inputProps.disabled,
        invalid: invalid,
        value: value
      })
    }), /*#__PURE__*/jsxs("div", {
      ref: inputTagsWrapperRef,
      className: "gz-input-tags-wrapper",
      children: [tagsToProcess.map(function (eachTag, index) {
        return /*#__PURE__*/jsx(Tag, {
          disabled: inputProps.disabled,
          closable: !inputProps.readOnly,
          onClose: function onClose() {
            return removeTag(index);
          },
          children: eachTag
        }, index);
      }), /*#__PURE__*/jsx("input", _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
        ref: innerInputRef,
        className: "gz-input",
        onFocus: onInputFocusHandler,
        onBlur: onInputBlurHandler,
        onChange: onInputChangeHandler,
        onKeyDown: onInputKeyDownHandler,
        placeholder: shouldShowPlaceholder ? placeholder : null // eslint-disable-next-line react/jsx-props-no-spreading

      }, inputProps), ariaAttributes), dataAttributes), inputFocusableProps), {}, {
        type: TYPES.TEXT,
        disabled: inputProps.disabled
      }))]
    }), suffixToProcess && /*#__PURE__*/jsx(Suffix, {
      children: suffixToProcess({
        disabled: inputProps.disabled,
        invalid: invalid,
        value: value
      })
    })]
  }));
});
InputTags$1.COMPONENT_ID = 'Gearz.Input.Tags';
InputTags$1.displayName = InputTags$1.COMPONENT_ID;
InputTags$1.propTypes = _objectSpread({}, TAGS_PROP_TYPES);
InputTags$1.defaultProps = _objectSpread({}, TAGS_DEFAULT_PROPS);

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var InputTags = styled(InputTags$1).withConfig({
  componentId: "gz-input-1675939611112__sc-1nm4zwc-0"
})(["background-color:#fff;border:1px solid #000;border-radius:4px;box-sizing:border-box;cursor:text;display:flex;line-height:20px;margin:0px;min-width:0;position:relative;width:100%;&[aria-disabled=\"true\"]{cursor:not-allowed;opacity:0.42;}.gz-input-tags-wrapper{align-items:center;display:flex;flex-basis:100%;flex-flow:row wrap;gap:6px;padding:4px 12px;.gz-tag{.gz-tag__close-button{opacity:1;&:focus,&:focus-visible{outline:2px solid #314fff;outline-offset:2px;}}}.gz-input{padding:0;min-width:6px;width:6px;&:first-child{width:100%;}&:disabled{pointer-events:none;}}}&:not(:focus-within){&:not([aria-disabled=\"true\"]){&[aria-invalid=\"true\"]{border-color:#f00314;&:focus,&:focus-visible,&:hover{border-color:#474b6d;}}}}", ""], function (props) {
  return getThemeByProps({
    componentId: InputTags$1.COMPONENT_ID,
    props: props
  });
});
InputTags.displayName = InputTags$1.displayName;
InputTags.propTypes = InputTags$1.propTypes;
InputTags.defaultProps = InputTags$1.defaultProps;

export { InputTags as I };
